// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Avatar, Box, Card, Group, Loader, Modal, Paper, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import type { Appointment, Encounter, Patient } from '@medplum/fhirtypes';
import { QuestionnaireResponseDisplay, useMedplum } from '@medplum/react';
import type { JSX, MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useIntakeFormState } from '../../hooks/useIntakeFormState';
import { useRole } from '../../hooks/useRole';
import { useSupplementalInformation } from '../../hooks/useSupplementalInformation';
import { isIntakeEncounter } from '../../utils/is-intake-encounter';
import { showErrorNotification } from '../../utils/notifications';
import { fetchPatientScreenerEntries, type ScreenerExportEntry } from '../../utils/patient-export';
import { hasPermission } from '../../utils/permissions';
import { IntakeScreenerModal } from './IntakeScreenerModal';

const BRAND = '#640040';

interface NameTagCardProps {
  title: string;
  initials: string;
  linkLabel: string;
  linkTo?: string;
  href?: string;
  fallbackLabel?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

function NameTagCard({
  title,
  initials,
  linkLabel,
  linkTo,
  href,
  fallbackLabel = 'Not available',
  onClick,
}: NameTagCardProps): JSX.Element {
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'white',
        border: `1px solid ${BRAND}`,
        borderRadius: 8,
        padding: 12,
        gap: 16,
      }}
    >
      <Avatar size={70} radius="xl" color="gray">
        {initials}
      </Avatar>
      <Stack gap={4}>
        <Text fw={700} fz="lg">
          {title}
        </Text>
        {linkTo && (
          <Anchor component={Link} to={linkTo} onClick={onClick} fw={600} fz="sm" td="underline" c={BRAND}>
            {linkLabel}
          </Anchor>
        )}
        {!linkTo && href && (
          <Anchor href={href} target="_blank" rel="noopener noreferrer" fw={600} fz="sm" td="underline" c={BRAND}>
            {linkLabel}
          </Anchor>
        )}
        {!linkTo && !href && (
          <Text fz="sm" c="dimmed">
            {fallbackLabel}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

interface IntakeScreenerCardProps {
  patient: Patient;
  encounter?: Encounter;
  appointment?: Appointment;
  onOpenIntake?: () => Promise<void>;
  onViewIntake?: () => void;
}

function IntakeScreenerCard({
  patient,
  encounter,
  appointment,
  onOpenIntake,
  onViewIntake,
}: IntakeScreenerCardProps): JSX.Element {
  const intakeState = useIntakeFormState(patient);
  const { role, isAdmin } = useRole();
  const canComplete = hasPermission({ role, permission: 'complete-intake-form', isAdmin });

  const title = 'Intake Screener';
  const initials = 'IS';

  if (intakeState === 'complete') {
    // 'View' routes to the existing chart-side screener page, which stays
    // read-only per the 2026-05-12 decision (chart screener never gains an
    // edit mode; the encounter-note path is the only place NPs complete).
    return (
      <NameTagCard
        title={title}
        initials={initials}
        linkLabel="View"
        linkTo={
          encounter?.id
            ? `/Patient/${patient.id}/Encounter/${encounter.id}/intake-screener`
            : `/Patient/${patient.id}/intake-screener`
        }
        onClick={
          onViewIntake
            ? (event) => {
                event.preventDefault();
                onViewIntake();
              }
            : undefined
        }
      />
    );
  }

  const unfinishedIntakeEncounter = isIntakeEncounter(encounter, appointment);
  if ((intakeState === 'awaiting-np' || unfinishedIntakeEncounter) && canComplete && encounter?.id) {
    return (
      <NameTagCard
        title={title}
        initials={initials}
        linkLabel="Complete"
        linkTo={`/Patient/${patient.id}/Encounter/${encounter.id}/intake-form`}
        onClick={
          onOpenIntake
            ? (event) => {
                event.preventDefault();
                onOpenIntake().catch(showErrorNotification);
              }
            : undefined
        }
      />
    );
  }

  return <NameTagCard title={title} initials={initials} linkLabel="" fallbackLabel="Not captured yet" />;
}

interface SupplementalInfoSectionProps {
  patient: Patient;
  encounter?: Encounter;
  appointment?: Appointment;
  onOpenIntake?: () => Promise<void>;
  onViewIntake?: () => void;
}

interface CompletedIntakeScreenerModalProps {
  patientId: string;
  opened: boolean;
  onClose: () => void;
}

export function CompletedIntakeScreenerModal({
  patientId,
  opened,
  onClose,
}: CompletedIntakeScreenerModalProps): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<ScreenerExportEntry[]>([]);

  useEffect(() => {
    if (!opened) {
      return;
    }
    setLoading(true);
    fetchPatientScreenerEntries(medplum, patientId)
      .then(setEntries)
      .catch(showErrorNotification)
      .finally(() => setLoading(false));
  }, [medplum, opened, patientId]);

  return (
    <Modal opened={opened} onClose={onClose} title="Completed Intake" size="xl">
      <ScrollArea.Autosize mah="70vh">
        {loading && <Loader />}
        {!loading && entries.length === 0 && <Text c="dimmed">No completed intake responses found.</Text>}
        {!loading &&
          entries.map(({ questionnaire, latestQR }) => (
            <Paper key={questionnaire.id} withBorder p="md" mb="md">
              <Title order={4} mb="sm">
                {questionnaire.title ?? questionnaire.name}
              </Title>
              <QuestionnaireResponseDisplay questionnaireResponse={latestQR} />
            </Paper>
          ))}
      </ScrollArea.Autosize>
    </Modal>
  );
}

export const SupplementalInfoSection = ({
  patient,
  encounter,
  appointment,
  onOpenIntake,
  onViewIntake,
}: SupplementalInfoSectionProps): JSX.Element => {
  const { hsatDiagnosticReport } = useSupplementalInformation(patient);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [completedIntakeOpen, setCompletedIntakeOpen] = useState(false);

  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient';
  const initials = patientName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Card withBorder p={20}>
      <Title order={2} mb={10}>
        Supplemental Information
      </Title>
      <Group grow align="flex-start" gap={8}>
        <IntakeScreenerCard
          patient={patient}
          encounter={encounter}
          appointment={appointment}
          onOpenIntake={onOpenIntake ?? (encounter?.id ? async () => setIntakeOpen(true) : undefined)}
          onViewIntake={onViewIntake ?? (encounter?.id ? () => setCompletedIntakeOpen(true) : undefined)}
        />
        <NameTagCard
          title={patientName}
          initials={initials}
          linkLabel="View Patient"
          linkTo={encounter?.id ? `/Patient/${patient.id}/Encounter/${encounter.id}/profile` : `/Patient/${patient.id}`}
        />
        <NameTagCard
          title="HSAT Results"
          initials="HR"
          linkLabel="View Report"
          href={hsatDiagnosticReport?.presentedForm?.[0]?.url}
        />
      </Group>
      <IntakeScreenerModal
        patient={patient}
        encounter={encounter}
        opened={intakeOpen}
        onClose={() => setIntakeOpen(false)}
      />
      <CompletedIntakeScreenerModal
        patientId={patient.id ?? ''}
        opened={completedIntakeOpen}
        onClose={() => setCompletedIntakeOpen(false)}
      />
    </Card>
  );
};
